import { useQuery } from '@tanstack/react-query'
import api from '../../services/api'

interface Subscription {
  id: number
  plan: string
  status: string
  current_period_start: string
  current_period_end: string
  trial_ends_at: string | null
}

interface UsageStats {
  leads_used: number
  leads_limit: number
  tokens_used: number
  tokens_limit: number
  channels_used: number
  channels_limit: number
}

const planLabels: Record<string, string> = {
  free: 'Бесплатный',
  trial: 'Пробный',
  starter: 'Стартовый',
  professional: 'Профессиональный',
  enterprise: 'Корпоративный',
}

const planPrices: Record<string, string> = {
  free: '0 USDT',
  trial: '0 USDT',
  starter: '29 USDT/мес',
  professional: '79 USDT/мес',
  enterprise: '199 USDT/мес',
}

export default function SubscriptionSettings() {
  const { data: subscription, isLoading: subLoading } = useQuery({
    queryKey: ['subscription'],
    queryFn: async () => {
      const response = await api.get<Subscription>('/subscriptions/current')
      return response.data
    },
  })

  const { data: usage, isLoading: usageLoading } = useQuery({
    queryKey: ['usageStats'],
    queryFn: async () => {
      const response = await api.get<UsageStats>('/subscriptions/usage')
      return response.data
    },
  })

  const isLoading = subLoading || usageLoading

  if (isLoading) {
    return (
      <div className="card flex justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    )
  }

  const formatDate = (date: string) => {
    return new Date(date).toLocaleDateString('ru-RU', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    })
  }

  return (
    <div className="space-y-6">
      {/* Current Plan */}
      <div className="card">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">
          Текущий план
        </h2>

        <div className="flex items-center justify-between mb-6">
          <div>
            <p className="text-2xl font-bold text-gray-900">
              {planLabels[subscription?.plan || 'free'] || subscription?.plan}
            </p>
            <p className="text-gray-600">
              {planPrices[subscription?.plan || 'free']}
            </p>
          </div>
          <span
            className={`px-3 py-1 rounded-full text-sm font-medium ${
              subscription?.status === 'active'
                ? 'bg-green-100 text-green-800'
                : subscription?.status === 'trialing'
                ? 'bg-blue-100 text-blue-800'
                : 'bg-gray-100 text-gray-800'
            }`}
          >
            {subscription?.status === 'active'
              ? 'Активна'
              : subscription?.status === 'trialing'
              ? 'Пробный период'
              : 'Неактивна'}
          </span>
        </div>

        {/* Period Info */}
        {subscription && (
          <div className="bg-gray-50 rounded-lg p-4 mb-6">
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <p className="text-gray-500">Начало периода</p>
                <p className="font-medium text-gray-900">
                  {formatDate(subscription.current_period_start)}
                </p>
              </div>
              <div>
                <p className="text-gray-500">Окончание периода</p>
                <p className="font-medium text-gray-900">
                  {formatDate(subscription.current_period_end)}
                </p>
              </div>
            </div>
            {subscription.trial_ends_at && (
              <div className="mt-4 pt-4 border-t border-gray-200">
                <p className="text-sm text-blue-600">
                  ⏰ Пробный период заканчивается: {formatDate(subscription.trial_ends_at)}
                </p>
              </div>
            )}
          </div>
        )}

        {/* Upgrade Button */}
        {subscription?.plan !== 'enterprise' && (
          <button className="btn-primary">
            Улучшить план
          </button>
        )}
      </div>

      {/* Usage Stats */}
      {usage && (
        <div className="card">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">
            Использование
          </h2>

          <div className="space-y-6">
            {/* Leads */}
            <UsageBar
              label="Лидов в день"
              used={usage.leads_used}
              limit={usage.leads_limit}
            />

            {/* Tokens */}
            <UsageBar
              label="AI токенов"
              used={usage.tokens_used}
              limit={usage.tokens_limit}
            />

            {/* Channels */}
            <UsageBar
              label="Каналов"
              used={usage.channels_used}
              limit={usage.channels_limit}
            />
          </div>
        </div>
      )}

      {/* Plans Comparison */}
      <div className="card">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">
          Доступные планы
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <PlanCard
            name="Стартовый"
            price="29 USDT"
            features={[
              '50 лидов/день',
              '10 каналов',
              '100K токенов',
              'Email поддержка',
            ]}
            current={subscription?.plan === 'starter'}
          />
          <PlanCard
            name="Профессиональный"
            price="79 USDT"
            features={[
              '200 лидов/день',
              '50 каналов',
              '500K токенов',
              'Приоритетная поддержка',
              'API доступ',
            ]}
            current={subscription?.plan === 'professional'}
            highlighted
          />
          <PlanCard
            name="Корпоративный"
            price="199 USDT"
            features={[
              'Безлимитные лиды',
              'Безлимитные каналы',
              '2M токенов',
              'Выделенный менеджер',
              'Кастомные интеграции',
            ]}
            current={subscription?.plan === 'enterprise'}
          />
        </div>
      </div>
    </div>
  )
}

function UsageBar({
  label,
  used,
  limit,
}: {
  label: string
  used: number
  limit: number
}) {
  const percent = limit === 0 ? 0 : Math.min(100, Math.round((used / limit) * 100))
  const isWarning = percent >= 80
  const isDanger = percent >= 95

  return (
    <div>
      <div className="flex justify-between text-sm mb-1">
        <span className="text-gray-700">{label}</span>
        <span className="text-gray-500">
          {used.toLocaleString()} / {limit.toLocaleString()}
        </span>
      </div>
      <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
        <div
          className={`h-full rounded-full transition-all ${
            isDanger
              ? 'bg-red-500'
              : isWarning
              ? 'bg-yellow-500'
              : 'bg-blue-500'
          }`}
          style={{ width: `${percent}%` }}
        />
      </div>
    </div>
  )
}

function PlanCard({
  name,
  price,
  features,
  current,
  highlighted,
}: {
  name: string
  price: string
  features: string[]
  current?: boolean
  highlighted?: boolean
}) {
  return (
    <div
      className={`rounded-lg p-4 ${
        highlighted
          ? 'border-2 border-blue-500 bg-blue-50'
          : 'border border-gray-200'
      }`}
    >
      <h3 className="font-semibold text-gray-900">{name}</h3>
      <p className="text-2xl font-bold text-gray-900 mt-1">
        {price}
        <span className="text-sm font-normal text-gray-500">/мес</span>
      </p>
      <ul className="mt-4 space-y-2">
        {features.map((feature, index) => (
          <li key={index} className="flex items-center gap-2 text-sm text-gray-600">
            <svg
              className="h-4 w-4 text-green-500"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M5 13l4 4L19 7"
              />
            </svg>
            {feature}
          </li>
        ))}
      </ul>
      <button
        className={`w-full mt-4 py-2 rounded-lg text-sm font-medium ${
          current
            ? 'bg-gray-100 text-gray-500 cursor-not-allowed'
            : highlighted
            ? 'bg-blue-600 text-white hover:bg-blue-700'
            : 'border border-gray-300 text-gray-700 hover:bg-gray-50'
        }`}
        disabled={current}
      >
        {current ? 'Текущий план' : 'Выбрать'}
      </button>
    </div>
  )
}
